import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { isEmpty } from "lodash";
import useApiCall from "../../hooks/useApiCall";
import { usePageMeta } from "../../contexts/PageMetaContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import DataModelingTableSidebar from "./DataModelingTableSidebar";
import { SLATE } from "../../constants/Colors";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import json from "react-syntax-highlighter/dist/cjs/languages/hljs/json";
import { docco } from "react-syntax-highlighter/dist/cjs/styles/hljs";

SyntaxHighlighter.registerLanguage("json", json);

const groupFieldsByCategory = (fields) =>
  fields.reduce((acc, curr) => {
    const group = curr.group || "Ungrouped";
    if (!acc[group]) acc[group] = {};
    acc[group][curr.name] = curr.definition;
    return acc;
  }, {});

export default function UserProfileDataModeling() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userProfileStructures, setUserProfileStructures] = useState([]);
  const [importDialog, setImportDialog] = useState(false);

  const fetchData = () => {
    if (!currentApp?.id) return;
    setLoading(true);
    makeRequest(`/v1/apps/${currentApp.id}/personalisation_config_details`, {
      method: "GET",
      authTokens: tokens,
    })
      .then((data) => {
        if (Array.isArray(data)) {
          setUserProfileStructures(data.filter((item) => item.model_type === "user_profile"));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageMeta({
      description: "Define and manage the user profile data model fields.",
      actions: (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setImportDialog(true)}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", px: 2, "&:hover": { boxShadow: "none" } }}
          >
            Import Config
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsSidebarOpen(true)}
            sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", px: 2, "&:hover": { boxShadow: "none" } }}
          >
            Add / Edit
          </Button>
        </Box>
      ),
    });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveRow = async (form) => {
    const res = await makeRequest(
      `/v1/apps/${currentApp.id}/personalisation_config_details/create_and_update`,
      { method: "POST", body: { personalisation_config_details: [form] }, authTokens: tokens }
    );
    if (res) fetchData();
    return res;
  };

  const handleDeleteRow = async (record) => {
    if (!record?.id) return;
    const res = await makeRequest(
      `/v1/apps/${currentApp.id}/personalisation_config_details/delete_record`,
      { method: "DELETE", body: { record_id: record.id }, authTokens: tokens }
    );
    if (res?.success) {
      setUserProfileStructures((prev) => prev.filter((item) => item.id !== record.id));
    }
  };

  const handleImportConfig = async () => {
    setImportDialog(false);
    setLoading(true);
    try {
      const res = await makeRequest(
        `/v1/apps/${currentApp.id}/personalisation_config_details/import_config`,
        { method: "POST", body: { import_app_code: "eduenroll" }, authTokens: tokens }
      );
      if (res?.status === "success" && res?.configs?.length > 0) {
        setUserProfileStructures(res.configs.filter((item) => !item.model_type || item.model_type === "user_profile"));
      }
    } catch {
      // error toasted by useApiCall
    } finally {
      setLoading(false);
    }
  };

  const userProfileFields = groupFieldsByCategory(userProfileStructures);

  if (loading) return <LoadingSpinner message="Loading User Profile Data Model..." subMessage="Fetching configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      {/* Import confirmation dialog */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Confirm Import</Typography>
          <IconButton size="small" onClick={() => setImportDialog(false)} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 2 }}>
            Are you sure you want to import the configuration? This will overwrite existing data.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setImportDialog(false)}
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handleImportConfig}
              sx={{ textTransform: "none", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}>
              Import
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {isSidebarOpen && (
        <DataModelingTableSidebar
          dataProfileStructures={userProfileStructures}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          title="User Profile Data Model Definition"
          modelType="user_profile"
          onSaveRow={handleSaveRow}
          onDeleteRow={handleDeleteRow}
        />
      )}

      {!isEmpty(userProfileFields) ? (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)" }}>
          <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
              User Profile Structure
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ borderRadius: "8px", overflow: "auto", border: `1px solid ${SLATE[200]}`, maxHeight: "calc(100vh - 280px)" }}>
              <SyntaxHighlighter
                language="json"
                style={docco}
                customStyle={{ margin: 0, padding: "16px", fontSize: "0.8125rem", lineHeight: "1.6", background: "#f8fafc", fontFamily: "ui-monospace, 'Cascadia Code', Menlo, Consolas, monospace" }}
                wrapLines wrapLongLines
              >
                {JSON.stringify(userProfileFields, null, 2)}
              </SyntaxHighlighter>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "12px", p: 6, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
            No user profile fields defined yet. Click <strong>Add / Edit</strong> to get started.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
